// zod-схемы входных данных REST API и событий Socket.IO (общие для сервера и клиента).
import { z } from 'zod';
import { HELP_KINDS } from './allianceHelp.js';
import { RALLY_WAIT_MINUTES } from './rally.js';
import {
  ALLIANCE_ROLES,
  BOMB_WIRES,
  BOOSTER_TYPES,
  BUILDING_TYPES,
  DIPLOMACY_TYPES,
  EQUIPMENT_SLOTS,
  HERO_CLASSES,
  HERO_TALENTS,
  type HeroTalent,
  MARCH_TYPES,
  RACES,
  RARITIES,
  RESOURCE_TYPES,
  SABOTAGE_TYPES,
  SEA_MARCH_TYPES,
  SHIP_TYPES,
} from './types.js';

export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const passwordSchema = z
  .string()
  .min(8)
  .max(128)
  .regex(/[0-9]/, 'password_digit')
  .regex(/[A-Za-zА-Яа-яЁё]/, 'password_letter');
export const nicknameSchema = z
  .string()
  .trim()
  .min(3)
  .max(20)
  .regex(/^[A-Za-zА-Яа-яЁё0-9_\-]+$/, 'nickname_chars');

const unitsSchema = z
  .record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000))
  .refine((u) => Object.values(u).some((n) => n > 0), 'empty_units');

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  nickname: nicknameSchema,
  race: z.enum(RACES),
  captchaToken: z.string().max(4096).optional(),
  referralCode: z.string().max(32).optional(),
  locale: z.enum(['ru', 'en', 'zh']).default('ru'),
  ageConfirmed: z.literal(true),
  acceptTerms: z.literal(true),
  fingerprint: z.string().max(128).optional(),
  /** Регистрация из Android-приложения: ссылка из письма вернёт игрока в приложение. */
  app: z
    .object({ v: z.number().int().min(1).max(100_000), store: z.enum(['site', 'play', 'rustore']) })
    .optional(),
});
/** Регистрация ждёт подтверждения почты: секрет этой регистрации → сессия, как только почта подтверждена. */
export const verifyPollSchema = z.object({ pollToken: z.string().length(64) });

/** Вход через платформу: data — initData Telegram, строка запуска VK, подписанные данные Яндекса
 *  или (oauth) билет сервера после входа через аккаунт Google / VK ID / Яндекс ID / Telegram. */
export const PLATFORMS = ['telegram', 'vk', 'yandex', 'oauth'] as const;
export type Platform = (typeof PLATFORMS)[number];
export const platformAuthSchema = z.object({
  platform: z.enum(PLATFORMS),
  data: z.string().min(10).max(8192),
});
export const platformRegisterSchema = platformAuthSchema.extend({
  nickname: nicknameSchema,
  race: z.enum(RACES),
  locale: z.enum(['ru', 'en', 'zh']).default('ru'),
  ageConfirmed: z.literal(true),
  acceptTerms: z.literal(true),
  referralCode: z.string().max(32).optional(),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(128),
  fingerprint: z.string().max(128).optional(),
});

export const emailOnlySchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({ token: z.string().length(64), newPassword: passwordSchema });
export const refreshSchema = z.object({ refreshToken: z.string().min(20).max(512).optional() });

/** Здание — по id (любое) или по типу (единственные: ратуша, стена, храм…). */
const buildingRef = z
  .object({
    buildingId: z.string().min(1).max(64).optional(),
    buildingType: z.enum(BUILDING_TYPES).optional(),
    buyMissing: z.boolean().optional(),
    /** Все строители заняты — нанять ещё одного за золото на эту стройку. */
    extraBuilder: z.boolean().optional(),
  })
  .refine((v) => v.buildingId || v.buildingType, { message: 'buildingId или buildingType' });
export const upgradeBuildingSchema = buildingRef;
/** Ускорение: бесплатно — последние секунды (freeUnderSec), иначе — буст (item) из запаса или купленный за золото (buy). */
const speedupOpts = {
  item: z
    .string()
    .regex(/^speedup_[a-z0-9]+$/)
    .max(30)
    .nullish(),
  buy: z.boolean().optional(),
  /** Оптимальный набор бустов (из запаса + докупка за золото) одной кнопкой. */
  auto: z.boolean().optional(),
};
/** Включить щит бустом из запаса или купить буст за золото и сразу включить (buy). */
export const packActivateSchema = z.object({
  code: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(40),
  count: z.number().int().min(1).max(9999),
  castleId: z.string().max(64),
});
export const shieldActivateSchema = z.object({
  /** Сколько щитов из запаса включить разом: их часы складываются. */
  count: z.number().int().min(1).max(9999).optional(),
  item: z
    .string()
    .regex(/^shield_boost_[a-z0-9_]+$/)
    .max(40),
  buy: z.boolean().optional(),
  /** Какой замок закрыть обычным щитом; без него главный. */
  castleId: z.string().max(64).nullish(),
  /** Щит гавани (у каждой гавани свой); без него — щит замков. */
  portId: z.string().max(64).nullish(),
});
/** Марш-бросок: буст армии к войскам в пути — из запаса или купить за золото (buy). */
export const marchBoostSchema = z.object({
  marchId: z.string().min(1).max(64),
  sea: z.boolean().optional(),
  item: z
    .string()
    .regex(/^march_boost_[a-z0-9]+$/)
    .max(40),
  buy: z.boolean().optional(),
});
/** Ускорение войск в пути (сухопутный или морской поход) бустами ускорения. */
export const speedupMarchSchema = z.object({
  marchId: z.string().min(1).max(64),
  sea: z.boolean().optional(),
  ...speedupOpts,
});
export const speedupBuildingSchema = z
  .object({
    buildingId: z.string().min(1).max(64).optional(),
    buildingType: z.enum(BUILDING_TYPES).optional(),
    useGems: z.boolean().optional(),
    ...speedupOpts,
  })
  .refine((v) => v.buildingId || v.buildingType, { message: 'buildingId или buildingType' });
export const buildBuildingSchema = z.object({
  castleId: z.string().max(64).nullish(),
  type: z.enum(BUILDING_TYPES),
  zone: z.enum(['castle', 'field']),
  plot: z.number().int().min(0).max(199),
  buyMissing: z.boolean().optional(),
  extraBuilder: z.boolean().optional(),
});
/** Новое название своего замка: 2–24 символа — буквы, цифры, пробел и - _ ' . */
export const castleRenameSchema = z.object({
  castleId: z.string().min(1).max(64),
  name: z
    .string()
    .trim()
    .min(2)
    .max(24)
    .regex(/^[\p{L}\p{N} _'.-]+$/u),
});
export const castleRaceSchema = z.object({
  castleId: z.string().min(1).max(64),
  race: z.enum(RACES),
});
export const unitUpgradeSchema = z.object({
  unitType: z
    .string()
    .regex(/^[a-z_0-9]+$/)
    .max(40),
  stat: z.enum(['attack', 'defense']),
  buyMissing: z.boolean().optional(),
  /** Замок, в котором улучшают (по умолчанию — выбранный в клиенте) */
  castleId: z.string().max(64).optional(),
  ...speedupOpts,
});
export const demolishBuildingSchema = z.object({ buildingId: z.string().min(1).max(64) });
export const trainSchema = z.object({
  castleId: z.string().max(64).nullish(),
  unitType: z.string().regex(/^[a-z_0-9]+$/),
  count: z.number().int().min(1).max(1_000_000),
  buyMissing: z.boolean().optional(),
});
export const speedupTrainSchema = z.object({ orderId: z.string().min(1).max(64), ...speedupOpts });
/** Ускорение улучшения флота (идёт одно — заказ указывать не нужно). */
export const speedupOnlySchema = z.object({ ...speedupOpts });
/** Улучшение флота в Верфи. */
export const fleetUpgradeSchema = z.object({
  stat: z.enum(['speed', 'attack', 'defense', 'patrol', 'troops']),
  buyMissing: z.boolean().optional(),
  /** Замок, в Верфи которого улучшают (по умолчанию — выбранный в клиенте) */
  castleId: z.string().max(64).optional(),
});

export const mapQuerySchema = z.object({
  x: z.coerce.number().int().min(0).max(999),
  y: z.coerce.number().int().min(0).max(999),
  radius: z.coerce.number().int().min(1).max(20).default(10),
});

/** Академия: начать/ускорить исследование. */
export const researchSchema = z.object({
  code: z
    .string()
    .regex(/^[a-z_]+$/)
    .max(30),
  ...speedupOpts,
  buyMissing: z.boolean().optional(),
  /** Замок, в академии которого исследуют (по умолчанию — выбранный в клиенте) */
  castleId: z.string().max(64).optional(),
});

const unitCounts = z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000));

/** Лазарет: вылечить раненых (все — если units не указан). */
/** Таверна: нанять отряд наёмников из предложений дня. */
export const mercenaryHireSchema = z.object({
  castleId: z.string().max(64).nullish(),
  offer: z.number().int().min(0).max(10),
  buyMissing: z.boolean().optional(),
});
export const healSchema = z.object({
  castleId: z.string().max(64).nullish(),
  units: unitCounts.nullish(),
  buyMissing: z.boolean().optional(),
});

/** Рынок: отправить ресурсы союзнику. */
export const marketSendSchema = z.object({
  toUserId: z.string().min(1).max(64),
  /** Замок союзника-получателя (нажали на него на карте); по умолчанию — его главный. */
  toCastleId: z.string().min(1).max(64).nullish(),
  resources: z.record(z.enum(RESOURCE_TYPES), z.number().int().min(0).max(100_000_000)),
});
export const marketBuyResourceSchema = z.object({
  resource: z.enum(RESOURCE_TYPES),
  gems: z.number().int().min(1).max(100_000),
});
export const marketExchangeSchema = z
  .object({
    from: z.enum(RESOURCE_TYPES),
    to: z.enum(RESOURCE_TYPES),
    amount: z.number().int().min(1).max(100_000_000),
  })
  .refine((v) => v.from !== v.to, 'same_resource');

export const marchSendSchema = z.object({
  /** Новичок подтвердил, что атака на чужой замок снимет его щит. */
  acceptShieldLoss: z.boolean().optional(),
  expectedTerrainKey: z.string().min(1).max(128).optional(),
  avoidDanger: z.boolean().optional(),
  acceptUnsafeRoute: z.boolean().optional(),
  /** Из какого замка (захваченного тоже); по умолчанию — главный. */
  fromCastleId: z.string().max(64).nullish(),
  type: z.enum(MARCH_TYPES),
  x: z.number().int().min(0).max(999),
  y: z.number().int().min(0).max(999),
  units: z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000)),
  heroId: z.string().max(64).nullish(),
  /** Разведка с диверсией (только разведка чужого замка). */
  sabotage: z.enum(SABOTAGE_TYPES).nullish(),
  /** Бомба: настоящий фитиль (два других — пустышки); не указан — наугад. */
  sabotageWire: z.enum(BOMB_WIRES).nullish(),
  /** Бомба: тип здания-цели или random. */
  sabotageTarget: z
    .string()
    .regex(/^[a-z_]+$/)
    .max(30)
    .nullish(),
  /** Груз похода «Перевозка» — ресурсы из замка отправления в другой свой замок. */
  resources: z.record(z.enum(RESOURCE_TYPES), z.number().int().min(0).max(1_000_000_000)).nullish(),
  /** Клиентское время отправки — для проверки «поход в прошлое» и повторов. */
  clientTs: z.number().int().optional(),
});
export const marchRecallSchema = z.object({
  marchId: z.string().min(1).max(64),
  /** Частичный отзыв: сколько каких войск вернуть (остальные продолжают поход или остаются на месте). */
  units: z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000)).nullish(),
  /** Частичный отзыв: герой уходит вместе с отзываемыми войсками (или один). */
  withHero: z.boolean().optional(),
});
/** Приказ армии на случай засады: прорываться (по умолчанию), отступить, бросить груз. */
export const ambushOrderSchema = z.object({
  marchId: z.string().min(1).max(64),
  order: z.enum(['breakthrough', 'retreat', 'pay']),
});
/** Перехват чужой армии в пути: отряд, герой и (если не успевает) марш-бросок при отправке. */
export const marchInterceptSchema = z.object({
  targetMarchId: z.string().min(1).max(64),
  fromCastleId: z.string().max(64).nullish(),
  units: z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000)),
  heroId: z.string().max(64).nullish(),
  boost: z
    .string()
    .regex(/^march_boost_[a-z0-9]+$/)
    .max(40)
    .nullish(),
  buyBoost: z.boolean().optional(),
  /** Отправить, хоть и не успевает: игрок ускорит отряд в пути. */
  late: z.boolean().optional(),
});
/** Штурм: организатор собирает армии в своём замке (castleId) и ведёт их на замок-цель (x, y). */
export const rallyCreateSchema = z.object({
  /** Новичок подтвердил, что атака на чужой замок снимет его щит. */
  acceptShieldLoss: z.boolean().optional(),
  x: z.number().int().min(0).max(999),
  y: z.number().int().min(0).max(999),
  castleId: z.string().max(64).nullish(),
  units: z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000)),
  heroId: z.string().max(64).nullish(),
  waitMinutes: z
    .number()
    .int()
    .refine((v) => (RALLY_WAIT_MINUTES as readonly number[]).includes(v)),
});
/** Вступить в штурм отрядом из своего замка. */
export const rallyJoinSchema = z.object({
  /** Новичок подтвердил, что атака на чужой замок снимет его щит. */
  acceptShieldLoss: z.boolean().optional(),
  rallyId: z.string().min(1).max(64),
  fromCastleId: z.string().max(64).nullish(),
  units: z.record(z.string().regex(/^[a-z_0-9]+$/), z.number().int().min(0).max(10_000_000)),
});
export const rallyIdSchema = z.object({ rallyId: z.string().min(1).max(64) });
/** Попросить союзников помочь с таймером (стройка — id здания, исследование — код, найм — id заказа). */
export const helpRequestSchema = z.object({
  kind: z.enum(HELP_KINDS),
  targetId: z.string().min(1).max(64),
});
/** Помочь союзникам: ids — выбранные запросы, без ids — всем сразу. */
/** Ответ на мини-игру обрывка карты сокровищ (формат зависит от игры). */
export const expeditionSolveSchema = z.object({
  index: z.number().int().min(0).max(20),
  answer: z.array(z.union([z.string().max(4), z.number().int().min(0).max(100)])).max(200),
});
export const helpGiveSchema = z.object({ ids: z.array(z.string().max(64)).max(100).optional() });
/** Гарнизон захваченного замка — домой (без units — все боевые войска замка). */
export const garrisonHomeSchema = z.object({
  castleId: z.string().min(1).max(64),
  units: z.record(z.string().max(40), z.number().int().min(0).max(10_000_000)).nullish(),
});

/** Переименование героя (за золото). */
export const heroRenameSchema = z.object({ name: z.string().trim().min(2).max(20) });
export const heroCreateSchema = z.object({
  name: z.string().trim().min(2).max(20),
  heroClass: z.enum(HERO_CLASSES),
  buyMissing: z.boolean().optional(),
  /** Нанять своего героя неосновному замку (нужен Храм героев castleHeroTempleLevel). */
  castleId: z.string().max(64).nullish(),
});
/** Распределение очков героя по навыкам. */
export const heroTalentsSchema = z.object(
  Object.fromEntries(HERO_TALENTS.map((t) => [t, z.number().int().min(0).max(500).optional()])) as Record<
    HeroTalent,
    z.ZodOptional<z.ZodNumber>
  >,
);
export const heroStatsSchema = z.object({
  strength: z.number().int().min(0).max(500).default(0),
  agility: z.number().int().min(0).max(500).default(0),
  intellect: z.number().int().min(0).max(500).default(0),
  endurance: z.number().int().min(0).max(500).default(0),
  luck: z.number().int().min(0).max(500).default(0),
});
export const heroSkillSchema = z.object({
  slotIndex: z.number().int().min(0).max(5),
  skillKey: z.string().max(40),
});
export const heroEquipSchema = z.object({ itemKey: z.string().max(60) });
export const heroUnequipSchema = z.object({ slot: z.enum(EQUIPMENT_SLOTS) });

const ITEM_LINES = ['assault', 'bulwark', 'balance', 'wanderer'] as const;
/** Ковка вещи из осколков: направление, редкость и слот, который игрок выбрал сам. */
export const craftItemSchema = z.object({
  line: z.enum(ITEM_LINES),
  rarity: z.enum(RARITIES),
  slot: z.enum(EQUIPMENT_SLOTS),
});
export const mergeFragmentsSchema = z.object({ line: z.enum(ITEM_LINES), rarity: z.enum(RARITIES) });
/** Слияние: три одинаковые вещи → одна следующей редкости. */
export const mergeItemsSchema = z.object({
  itemKey: z
    .string()
    .regex(/^[a-z_]+$/)
    .max(64),
});

export const campaignAttackSchema = z.object({ units: unitsSchema, heroId: z.string().max(64).nullish() });

export const allianceCreateSchema = z.object({
  name: z.string().trim().min(3).max(30),
  tag: z
    .string()
    .trim()
    .min(2)
    .max(5)
    .regex(/^[A-Za-zА-Яа-яЁё0-9]+$/),
  description: z.string().trim().max(500).default(''),
  buyMissing: z.boolean().optional(),
});
export const allianceRoleSchema = z.object({ userId: z.string().max(64), role: z.enum(ALLIANCE_ROLES) });
export const allianceUserSchema = z.object({ userId: z.string().max(64) });
export const allianceInviteSchema = z.union([
  z.object({ userId: z.string().trim().min(1).max(64) }),
  z.object({ nickname: nicknameSchema }),
]);
export const allianceDonateSchema = z.object({
  resource: z.enum(RESOURCE_TYPES),
  amount: z.number().int().min(1).max(1e9),
});
export const diplomacySchema = z.object({
  targetAllianceId: z.string().max(64),
  type: z.enum(DIPLOMACY_TYPES).exclude(['coalition']),
  days: z.number().int().min(1).max(30).optional(),
  terms: z
    .object({
      tribute: z.record(z.enum(RESOURCE_TYPES), z.number().int().min(0)).optional(),
      noAttackDays: z.number().int().min(0).max(30).optional(),
    })
    .optional(),
});
export const taxSchema = z.object({ rate: z.number().int().min(1).max(15) });
export const coalitionCreateSchema = z.object({ name: z.string().trim().min(3).max(30) });
export const coalitionInviteSchema = z.object({ allianceId: z.string().max(64) });
export const treasuryProposalSchema = z.object({
  resource: z.enum(RESOURCE_TYPES),
  amountPerMember: z.number().int().min(1).max(1e9),
});
export const treasuryVoteSchema = z.object({ yes: z.boolean() });
/** Выдача из казны одному участнику (лидер или казначей). */
export const treasuryPayoutSchema = z.object({
  toUserId: z.string().min(1).max(64),
  resources: z.record(z.enum(RESOURCE_TYPES), z.number().int().min(0).max(1_000_000_000)),
});

export const migrateSchema = z.object({
  toWorldId: z.number().int().positive(),
  paid: z.boolean().default(false),
});

/** Картинка в сообщении: имя файла после загрузки (POST /media/chat) и размеры для разметки. */
/** Картинка в ленте: имя файла (ссылка /api/v1/media/chat/<image>) и размеры. */
export interface ChatImageView {
  image: string;
  w: number | null;
  h: number | null;
}
/** Цитата сообщения, на которое ответили. */
export interface ReplyPreview {
  id: string;
  fromId: string | null;
  nickname: string;
  text: string;
  image: boolean;
}

export const chatImageSchema = z.object({
  image: z.string().max(64),
  w: z.number().int().min(1).max(10000).nullable().optional(),
  h: z.number().int().min(1).max(10000).nullable().optional(),
});
/** Сообщение в чат: текст, ответ на сообщение (цитата) и картинка; без текста — только с картинкой. */
export const chatSendSchema = z
  .object({
    text: z.string().trim().max(300).default(''),
    replyToId: z.string().max(64).optional(),
    image: chatImageSchema.optional(),
  })
  .refine((d) => d.text.length > 0 || !!d.image, { message: 'empty_message' });
export const privateChatSchema = z.object({
  toUserId: z.string().max(64),
  text: z.string().trim().min(1).max(300),
});
export const reportSchema = z.object({
  targetUserId: z.string().max(64),
  messageId: z.string().max(64).optional(),
  reason: z.enum(['spam', 'abuse', 'cheating', 'nickname', 'other']),
  comment: z.string().max(500).default(''),
});
export const friendRequestSchema = z.object({ nickname: nicknameSchema });
export const mailSendSchema = z
  .object({
    /** Кому: ник (новая переписка) или id собеседника (ответ в чате). */
    toNickname: nicknameSchema.optional(),
    toId: z.string().max(64).optional(),
    /** Тема — у писем; в чате не нужна. */
    subject: z.string().trim().max(80).optional().default(''),
    body: z.string().trim().max(2000).default(''),
    /** Ответ на письмо этой переписки и картинка. */
    replyToId: z.string().max(64).optional(),
    image: chatImageSchema.optional(),
  })
  .refine((d) => !!d.toNickname || !!d.toId, { message: 'recipient_required' })
  .refine((d) => d.body.length > 0 || !!d.image, { message: 'empty_message' });

export const shopBuySchema = z.object({ code: z.string().max(40) });
export const paymentCreateSchema = z.object({
  code: z.string().max(40),
  provider: z.enum(['yookassa', 'stripe', 'telegram', 'googleplay', 'apple', 'test']),
  currency: z.enum(['RUB', 'USD', 'XTR']).optional(),
  receipt: z.string().max(20000).optional(),
  /** Почта для чека 54-ФЗ (ЮKassa), если в аккаунте её нет. */
  email: z.string().trim().email().max(254).optional(),
});
export const battlePassClaimSchema = z.object({
  level: z.number().int().min(1).max(100),
  premium: z.boolean(),
});

/** Гавань: без координат — у ближайшего свободного берега от главного замка. */
export const seaPortSchema = z.object({
  x: z.number().int().min(0).max(999).optional(),
  y: z.number().int().min(0).max(999).optional(),
  buyMissing: z.boolean().optional(),
});
export const shipBuildSchema = z.object({
  shipType: z.enum(SHIP_TYPES),
  count: z.number().int().min(1).max(20),
  buyMissing: z.boolean().optional(),
  /** Замок, на Верфи которого строят (по умолчанию — выбранный в клиенте) */
  castleId: z.string().max(64).optional(),
});
/** Найм капитана, создание альянса — недостающее серебро можно докупить за золото. */
export const buyMissingSchema = z.object({ buyMissing: z.boolean().optional() });
export const fleetSchema = z.object({
  name: z.string().trim().min(2).max(24),
  ships: z.record(z.enum(SHIP_TYPES), z.number().int().min(0).max(9)),
  flagship: z.enum(SHIP_TYPES).nullable().default(null),
  captainId: z.string().max(64).nullable().default(null),
  heroId: z.string().max(64).nullable().default(null),
});
export const seaMarchSchema = z.object({
  /** Новичок подтвердил, что атака на чужой замок снимет его щит. */
  acceptShieldLoss: z.boolean().optional(),
  /** Уже существующий отряд (например, стоящий в чужом порту после торговли). */
  fleetId: z.string().max(64).optional(),
  /** Новый поход: корабли со склада гавани и герой-командир флота. */
  ships: z.record(z.enum(SHIP_TYPES), z.number().int().min(0).max(999)).optional(),
  commanderId: z.string().max(64).nullish(),
  /** Гавань отправления (своя); по умолчанию — ближайшая к главному замку. */
  portId: z.string().max(64).nullish(),
  type: z.enum(SEA_MARCH_TYPES),
  targetPortId: z.string().max(64).optional(),
  x: z.number().int().min(0).max(999).optional(),
  y: z.number().int().min(0).max(999).optional(),
  /** Доставка: ресурсы на борт (из замка гавани отправления). */
  cargo: z.record(z.string().max(20), z.number().int().min(0).max(100_000_000)).default({}),
  /** Морской десант: войска на борт, поход после высадки и герой отряда. */
  troops: z.record(z.string().max(32), z.number().int().min(0).max(1_000_000)).optional(),
  landType: z.enum(['attack', 'capture']).optional(),
  heroId: z.string().max(64).nullish(),
  coastX: z.number().int().min(0).max(999).optional(),
  coastY: z.number().int().min(0).max(999).optional(),
  combatOrder: z.enum(['protect', 'warships', 'escape']).default('protect'),
  patrolRadius: z.number().int().min(8).max(60).default(10),
  patrolHours: z.number().int().min(1).max(168).default(2),
  /** Перехват плывущего чужого флота с карты: короткий дозор в точке встречи, без припасов. */
  interceptFleetId: z.string().max(64).optional(),
  evacuationId: z.string().max(64).optional(),
});
export const marketTradeSchema = z.object({
  portId: z.string().max(64),
  goods: z.string().max(20),
  count: z.number().int().min(1).max(100000),
  fleetId: z.string().max(64),
});
export const bountySchema = z.object({
  targetNickname: nicknameSchema,
  rewardGold: z.number().int().min(1000).max(1e9),
});

export const accountDeleteSchema = z.object({ password: z.string().min(1).max(128) });
export const settingsSchema = z.object({
  locale: z.enum(['ru', 'en', 'zh']).optional(),
  theme: z.enum(['dark', 'light']).optional(),
  emailOptIn: z.boolean().optional(),
  pushCategories: z.array(z.enum(['attack', 'building', 'alliance', 'event', 'ruling', 'mail'])).optional(),
  analyticsConsent: z.boolean().optional(),
});
export const raceChangeSchema = z.object({ race: z.enum(RACES) });
export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
});

// ───────────── Socket.IO: клиент → сервер (C2S) ─────────────
export const c2s = {
  'march:send': z.object({
    units: z.record(z.string(), z.number().int().min(0)),
    target: z.object({ x: z.number().int().min(0).max(999), y: z.number().int().min(0).max(999) }),
    heroId: z.string().max(64).nullish(),
    type: z.enum(MARCH_TYPES).default('attack'),
  }),
  'building:upgrade': z.object({
    buildingId: z.string().max(64).optional(),
    buildingType: z.enum(BUILDING_TYPES).optional(),
  }),
  'army:train': z.object({ unitType: z.string().max(30), count: z.number().int().min(1).max(1_000_000) }),
  'chat:alliance': chatSendSchema,
  'chat:global': chatSendSchema,
  'chat:coalition': chatSendSchema,
  'chat:group': chatSendSchema.and(z.object({ chatId: z.string().max(64) })),
  'chat:private': privateChatSchema,
  'market:buy': z.object({
    portId: z.string().max(64),
    goods: z.string().max(20),
    count: z.number().int().min(1).max(100000),
    fleetId: z.string().max(64),
  }),
  'fleet:send': z.object({
    fleetId: z.string().max(64),
    routeId: z.string().max(64),
    cargo: z.record(z.string(), z.number().int().min(0)).default({}),
  }),
  'coalition:invite': z.object({ allianceId: z.string().max(64) }),
  'tax:protest': z.object({}).strict(),
  /** Набираю письмо собеседнику (typing=false — перестал или отправил). */
  'mail:typing': z.object({ toId: z.string().min(1).max(64), typing: z.boolean().default(true) }),
  ping: z.object({}).passthrough(),
} as const;
export type C2SEvent = keyof typeof c2s;
export type C2SPayload<E extends C2SEvent> = z.infer<(typeof c2s)[E]>;

// ───────────── Socket.IO: сервер → клиент (S2C) ─────────────
export interface S2CEvents {
  'march:update': { marchId: string; status: string; arriveAt: string };
  'march:arrived': { marchId: string; battleId: string | null };
  /** Штурм изменился (создан, кто-то вступил/вышел, выступил, закончился). */
  'rally:update': { rallyId: string };
  /** Помощь альянса: появился/закрылся запрос или вам помогли. */
  'alliance:help': { requestId?: string };
  /** Общая история изменилась; без личных уведомлений о победе/поражении союзника. */
  'alliance:history': Record<string, never>;
  /** Экспедиционный корабль: вернулся, нашёл клад, карта собрана. */
  'expedition:update': Record<string, never>;
  'battle:report': {
    battleId: string;
    attackerId?: string | null;
    defenderId?: string | null;
    report: { winner: string; attacker: string; defender: string; x: number; y: number };
  };
  'building:done': { buildingId: string; buildingType: string; level: number };
  'army:trained': { unitType: string; count: number };
  'resources:update': {
    wood: number;
    stone: number;
    food: number;
    iron: number;
    silver: number;
    gems: number;
    rates?: Record<string, number>;
    capacity?: number;
    /** Замок, к которому относятся ресурсы (у каждого замка свои). */
    castleId?: string;
    at: string;
  };
  'chat:message': {
    id: string;
    /** id null — строка от системы («X добавил Y» в групповом чате). */
    from: { id: string | null; nickname: string; allianceTag: string | null; title: string | null };
    channel: string;
    /** Групповой чат, к которому относится сообщение. */
    chatId?: string;
    text: string;
    ts: string;
    image?: ChatImageView | null;
    replyTo?: ReplyPreview | null;
  };
  'chat:deleted': { id: string; channel: string };
  notification: { type: string; payload: Record<string, unknown> };
  /** Собеседник получил или прочитал мои письма — обновить галочки в переписке с ним. */
  'mail:status': { peerId: string };
  /** Собеседник набирает письмо мне (как «печатает…» в мессенджерах). */
  'mail:typing': { fromId: string; typing: boolean };
  'map:update': {
    tiles: {
      x: number;
      y: number;
      ownerId: string | null;
      objectType: string | null;
      level?: number;
      id?: string;
      burningUntil?: string;
      reductionPct?: number;
    }[];
  };
  'map:combat': {
    kind: 'tower_fire' | 'tower_siege' | 'tower_assault';
    worldId: number;
    marchId: string;
    towerId: string;
    from: { x: number; y: number };
    to: { x: number; y: number };
    at: number;
    damage?: number;
    losses?: number;
    destroyed?: boolean;
  };
  'online:count': { total: number; alliance: number };
  'attack:incoming': { fromUser: string; arriveAt: string; marchId: string; x: number; y: number };
  'market:update': { portId: string; prices: Record<string, number> };
  'ruling:changed': {
    newAllianceId: string | null;
    oldAllianceId: string | null;
    newAllianceName?: string;
    reason: string;
  };
  'tax:charged': { amount: number; resourceType: string; rate: number };
  'coalition:formed': { coalitionId: string; allianceIds: string[] };
  'capital:under_attack': { attackerAllianceId: string | null; arriveAt: string };
  'quest:progress': { key: string; progress: number; target: number; completed: boolean };
  'hero:levelup': { heroId: string; level: number };
  'event:started': { type: string; endsAt: string };
  'system:broadcast': { text: string; level: 'info' | 'warning' };
  'session:revoked': { reason: string };
  pong: { ts: number };
}

/** Закладка на карте мира. */
export const bookmarkSchema = z.object({
  x: z.number().int().min(0).max(999),
  y: z.number().int().min(0).max(999),
  name: z.string().trim().min(1).max(40),
});

/** Переселение замка в клетку (x, y). */
export const castleRelocateSchema = z.object({
  castleId: z.string().min(1).max(64),
  x: z.number().int().min(0).max(999),
  y: z.number().int().min(0).max(999),
});

/** Лот торговой площадки: отдаю give × giveAmount, хочу want × wantAmount. */
export const tradeLotSchema = z
  .object({
    give: z.enum(RESOURCE_TYPES),
    giveAmount: z.number().int().min(100).max(10_000_000),
    want: z.enum(RESOURCE_TYPES),
    wantAmount: z.number().int().min(100).max(10_000_000),
  })
  .refine((v) => v.give !== v.want, { message: 'same_resource' });

/**
 * Состав товара магазина (набора): всё, что игрок получит сразу после покупки. Коды ускорений, щитов,
 * марш-бросков и войск проверяет сервер по балансу; служебные ключи vip/days/battlePass/skin — для особых товаров.
 */
const countMap = z.record(z.string().regex(/^[a-z0-9_]+$/), z.number().int().min(1).max(1_000_000));
export const shopRewardsSchema = z
  .object({
    gems: z.number().int().min(1).max(10_000_000).optional(),
    silver: z.number().int().min(1).max(1_000_000_000).optional(),
    wood: z.number().int().min(1).max(1_000_000_000).optional(),
    stone: z.number().int().min(1).max(1_000_000_000).optional(),
    food: z.number().int().min(1).max(1_000_000_000).optional(),
    iron: z.number().int().min(1).max(1_000_000_000).optional(),
    speedups: countMap.optional(),
    shields: countMap.optional(),
    marchBoosts: countMap.optional(),
    relocations: z.number().int().min(1).max(100).optional(),
    islandRelocations: z.number().int().min(1).max(100).optional(),
    shieldHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 60)
      .optional(),
    units: countMap.optional(),
    boosters: z.array(z.enum(BOOSTER_TYPES)).max(10).optional(),
    heroXp: z.number().int().min(1).max(100_000_000).optional(),
    bpXp: z.number().int().min(1).max(100_000_000).optional(),
    vip: z.number().int().min(1).max(10).optional(),
    days: z.number().int().min(1).max(365).optional(),
    battlePass: z.boolean().optional(),
    skin: z.string().max(40).optional(),
    migration: z.boolean().optional(),
    raceChange: z.boolean().optional(),
    items: z.array(z.string().max(64)).max(20).optional(),
  })
  .strict();
export type ShopRewardsInput = z.infer<typeof shopRewardsSchema>;

/** Скин замка: castleId null — на все замки; days null — навсегда. */
export const castleSkinBuySchema = z.object({
  skin: z.string().max(40),
  days: z.number().int().positive().nullable(),
  castleId: z.string().max(64).nullable(),
});
export const castleSkinEquipSchema = z.object({
  id: z.string().max(64),
  castleId: z.string().max(64).nullable(),
});
